import './DashboardScreen.scss';

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from 'moment';

import { getAlarmStats } from 'src/module/alarm/selector';
import { fetchAlarmStats, fetchDevicesInAlarmWithoutNotification } from 'src/module/alarm/action';
import { fetchMetrics } from 'src/module/facility/action';
import { fetchNewWorkTicketsLast7Days } from 'src/module/workTicket/action';

import { getAuthenticatedUser, getClient, getClientId, getCriticalityLabels } from 'src/module/authentication/selector';
import { updateClient } from 'src/module/authentication/action';

import FilterBar from 'src/component/UI/FilterBar';
import DashboardStatistics from 'src/component/UI/Statistics/DashboardStatistics';
import DashboardEditor from 'src/component/DashboardEditor/DashboardEditor';
import ConfirmModal from 'src/component/UI/ConfirmModal';
import FeatureFence from 'src/component/FeatureFence';
import { updateUser } from 'src/module/user/action';
import { isWhitelabel } from 'src/utils/utils';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import { useTranslation } from 'src/i18n';
import { buildDefaultLayout } from 'src/component/DashboardEditor/defaultDashboardLayout';
import { assignEditorIds, stripEditorIds } from 'src/component/DashboardEditor/utils';

export default function DashboardScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);
  const clientId = useSelector(getClientId);
  const client = useSelector(getClient);
  const alarmStats = useSelector(getAlarmStats);
  const user = useSelector(getAuthenticatedUser);
  const unverifiedContact = user.get('unverified_contact_number');

  const criticalityLabels = useSelector(getCriticalityLabels);
  const highestCriticality = (criticalityLabels?.size ?? 0) > 0 ? criticalityLabels.size - 1 : 0;

  const location = useLocation<{ editDashboard?: boolean }>();
  const history = useHistory();

  const [isEditMode, setIsEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState<any[] | null>(null);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAlarmStats(selectedFilters, null, null, null));
    dispatch(fetchMetrics());
    dispatch(fetchDevicesInAlarmWithoutNotification(Map(selectedFilters), undefined, highestCriticality));
    dispatch(fetchNewWorkTicketsLast7Days(Map(selectedFilters)));
  }, [clientId, selectedFilters, highestCriticality]);

  useEffect(() => {
    if (unverifiedContact && unverifiedContact !== user?.get('contact_number')) {
      dispatch(updateUser({ contact_number: unverifiedContact, unverified_contact_number: null }));
    }
  }, [unverifiedContact]);

  useEffect(() => {
    if (location.state?.editDashboard) {
      handleEdit();
      history.replace('/', {});
    }
  }, []);

  const handleEdit = () => {
    const currentLayout = client.getIn(['preferences', 'dashboard', 'layout'], null);
    const effectiveLayout = currentLayout ? currentLayout.toJS() : buildDefaultLayout(t);
    setDraftLayout(assignEditorIds(JSON.parse(JSON.stringify(effectiveLayout))));
    setIsEditMode(true);
  };

  const handleSaveRequest = () => {
    setConfirmSaveOpen(true);
  };

  const handleSaveConfirm = () => {
    const cleaned = stripEditorIds(draftLayout);
    const clientName = client.get('name');
    dispatch(updateClient(clientName, Map({ dashboard: Map({ layout: cleaned }) })));
    setConfirmSaveOpen(false);
    setIsEditMode(false);
    setDraftLayout(null);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setDraftLayout(null);
  };

  const handleReset = () => {
    const clientName = client.get('name');
    dispatch(updateClient(clientName, Map({ dashboard: Map({ layout: null }) })));
    setIsEditMode(false);
    setDraftLayout(null);
  };

  const freshnessDate = alarmStats.getIn(['dataFreshness', 'overall']);
  const freshnessNote = (freshnessDate) ? moment(freshnessDate).format('YYYY-MM-DD hh:mm A') : 'N/A';
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  return (
    <div id='dashboard' className='page'>
      <Helmet>
        <title>{title} - {t('dashboard.title')}</title>
      </Helmet>

      <Grid container>
        <Grid item xs={12} md={12}>
          <h3>{t('dashboard.systemOverview')} <FeatureFence can='eht-process-data'><span>{t('dashboard.byUnit')}</span></FeatureFence>
            <Tooltip title={t('dashboard.lastUpdatedAt', { time: freshnessNote })}>
              <AccessTimeIcon className='freshness' style={{ fontSize: '1.5rem', verticalAlign: 'middle', marginLeft: '4px', cursor: 'pointer' }} />
            </Tooltip>
          </h3>
        </Grid>
        {!isEditMode && (
          <FilterBar includeGrid />
        )}
      </Grid>
      {isEditMode ? (
        <>
          <DashboardEditor
            layout={draftLayout}
            onLayoutChange={setDraftLayout}
            onSave={handleSaveRequest}
            onCancel={handleCancel}
            onReset={handleReset}
          />
          <ConfirmModal
            open={confirmSaveOpen}
            title={t('dashboard.confirmSaveTitle')}
            message={t('dashboard.confirmSaveMessage', { facilityName: client.get('name') })}
            handleConfirm={handleSaveConfirm}
            handleReject={() => setConfirmSaveOpen(false)}
            handleClose={() => setConfirmSaveOpen(false)}
          />
        </>
      ) : (
        <div className='innerElements'>
          <DashboardStatistics />
        </div>
      )}
    </div>
  );
}
